import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { api, setAuthToken } from '../lib/api'

export type AuthUser = {
  id: string
  role: 'HEAD_TEACHER' | 'PRINCIPAL' | 'TUTOR' | 'STUDENT'
  firstName: string
  lastName: string
  email?: string
  schoolId: string
  avatarUrl?: string | null
  type: 'user' | 'child'
}

type AuthState = {
  user: AuthUser | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean

  login: (email: string, password: string, totpCode?: string) => Promise<void>
  pinLogin: (childId: string, pin: string) => Promise<void>
  logout: () => void
  setTokens: (access: string, refresh: string) => void
  hydrate: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,

      hydrate: () => {
        const { accessToken } = get()
        if (accessToken) setAuthToken(accessToken)
      },

      setTokens: (access, refresh) => {
        setAuthToken(access)
        set({ accessToken: access, refreshToken: refresh })
      },

      login: async (email, password, totpCode?) => {
        const { data } = await api.post('/auth/login', { email, password, ...(totpCode && { totpCode }) })
        const { accessToken, refreshToken, user } = data.data
        setAuthToken(accessToken)
        set({
          user: { ...user, type: 'user' as const },
          accessToken,
          refreshToken,
          isAuthenticated: true,
        })
      },

      pinLogin: async (childId, pin) => {
        const { data } = await api.post('/auth/login/pin', { childId, pin })
        const { accessToken, refreshToken, child } = data.data
        setAuthToken(accessToken)
        set({
          user: { ...child, type: 'child' as const },
          accessToken,
          refreshToken,
          isAuthenticated: true,
        })
      },

      logout: () => {
        const { refreshToken } = get()
        if (refreshToken) {
          api.post('/auth/logout', { refreshToken }).catch(() => {})
        }
        setAuthToken(null)
        set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false })
      },
    }),
    {
      name: 'sprouthill-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        user: s.user,
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        isAuthenticated: s.isAuthenticated,
      }),
    },
  ),
)

